import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RatioWireIcon } from './WorkflowAspectRatioField';
import {
  OUTPAINT_EXPAND_MODES,
  OUTPAINT_AR_PRESETS,
  OUTPAINT_SCALE_OPTIONS,
  getOutpaintPresetDimensions,
} from '@/pages/ipe/workbench/constants/imageFissionAspectSize';
import {
  clampOutpaintAspectSide,
  normalizeOutpaintCustomAspect,
  OUTPAINT_CUSTOM_AR_MAX,
  OUTPAINT_CUSTOM_AR_MIN,
} from '@/pages/ipe/workbench/utils/outpaintCustomAspectRatio';
import {
  clampOutpaintScale,
  OUTPAINT_SCALE_MAX,
  OUTPAINT_SCALE_MIN,
} from '@/pages/ipe/workbench/utils/outpaintExpandScale';

export default function OutpaintExpandParamsField({ value, onChange, readOnly = false }) {
  const { t } = useTranslation('workbench');
  const expandMode = Number(value?.expandMode) === 2 ? 2 : 1;
  const scaleStored = Number(value?.scale);
  const scale = Number.isFinite(scaleStored) && scaleStored > 1 ? scaleStored : 1.5;
  const aspectRatio = String(value?.aspectRatio || '1:1');
  const isCustomAr = aspectRatio === 'custom';
  const isScaleMode = expandMode === 1;
  const isCustomScale = value?.scaleCustom === true;
  const [scaleDraft, setScaleDraft] = useState('');

  const presetMeta = useMemo(
    () => OUTPAINT_AR_PRESETS.find((item) => item.value === aspectRatio) || null,
    [aspectRatio],
  );

  const iconRatio = useMemo(() => {
    if (presetMeta) return presetMeta;
    const width = clampOutpaintAspectSide(value?.outputWidth ?? 1);
    const height = clampOutpaintAspectSide(value?.outputHeight ?? 1);
    return { w: width, h: height };
  }, [presetMeta, value?.outputHeight, value?.outputWidth]);

  const patch = useCallback((next) => {
    onChange?.(next);
  }, [onChange]);

  useEffect(() => {
    if (!isCustomScale) {
      setScaleDraft('');
      return;
    }
    setScaleDraft((prev) => (prev === '' ? String(value?.scale ?? scale) : prev));
  }, [isCustomScale, value?.scale, scale]);

  const commitScaleDraft = useCallback(() => {
    const trimmed = String(scaleDraft ?? '').trim();
    if (!trimmed) {
      const fallback = clampOutpaintScale(scale);
      setScaleDraft(String(fallback));
      patch({ scale: fallback, scaleCustom: true });
      return;
    }
    const clamped = clampOutpaintScale(trimmed);
    setScaleDraft(String(clamped));
    patch({ scale: clamped, scaleCustom: true });
  }, [patch, scale, scaleDraft]);

  const setMode = (mode) => {
    if (readOnly || mode === expandMode) return;
    if (mode === 2) {
      const dims = getOutpaintPresetDimensions('1:1');
      patch({
        expandMode: 2,
        aspectRatio: '1:1',
        outputWidth: dims?.width ?? 1024,
        outputHeight: dims?.height ?? 1024,
      });
      return;
    }
    patch({ expandMode: 1, scale: scale > 1 ? scale : 1.5, scaleCustom: false });
  };

  const selectAr = (next) => {
    if (readOnly) return;
    if (next === 'custom') {
      const normalized = normalizeOutpaintCustomAspect(
        value?.outputWidth ?? 1,
        value?.outputHeight ?? 1,
      );
      patch({
        aspectRatio: 'custom',
        outputWidth: normalized.outputWidth,
        outputHeight: normalized.outputHeight,
      });
      return;
    }
    const dims = getOutpaintPresetDimensions(next);
    patch({
      aspectRatio: next,
      outputWidth: dims?.width ?? value?.outputWidth ?? 1024,
      outputHeight: dims?.height ?? value?.outputHeight ?? 1024,
    });
  };

  const handleScalePreset = (nextScale) => {
    if (readOnly) return;
    patch({ scale: nextScale, scaleCustom: false });
  };

  const handleScaleCustom = () => {
    if (readOnly || isCustomScale) return;
    const next = scale > 1 ? scale : 1.2;
    setScaleDraft(String(next));
    patch({ scaleCustom: true, scale: next });
  };

  const handleScaleInput = (event) => {
    const next = event.target.value;
    setScaleDraft(next);
    const n = Number(next);
    if (Number.isFinite(n) && n > 1 && n <= OUTPAINT_SCALE_MAX) {
      patch({ scale: n, scaleCustom: true });
    }
  };

  const handleWidthChange = (event) => {
    const nextWidth = clampOutpaintAspectSide(event.target.value);
    if (!nextWidth) return;
    const nextHeight = clampOutpaintAspectSide(value?.outputHeight) || nextWidth;
    const preset = getOutpaintPresetDimensions(aspectRatio);
    const matchesPreset = preset && preset.width === nextWidth && preset.height === nextHeight;
    patch({
      outputWidth: nextWidth,
      outputHeight: nextHeight,
      aspectRatio: matchesPreset ? aspectRatio : 'custom',
    });
  };

  const handleHeightChange = (event) => {
    const nextHeight = clampOutpaintAspectSide(event.target.value);
    if (!nextHeight) return;
    const nextWidth = clampOutpaintAspectSide(value?.outputWidth) || nextHeight;
    const preset = getOutpaintPresetDimensions(aspectRatio);
    const matchesPreset = preset && preset.width === nextWidth && preset.height === nextHeight;
    patch({
      outputWidth: nextWidth,
      outputHeight: nextHeight,
      aspectRatio: matchesPreset ? aspectRatio : 'custom',
    });
  };

  return (
    <div className="outpaint-expand-params">
      <div
        className="wf-editor-aiprod__mode-bar ai-product-image-page__mode-bar outpaint-expand-params__mode-bar"
        role="tablist"
        aria-label={t('workflowParams.expandAria')}
      >
        {OUTPAINT_EXPAND_MODES.map((mode) => (
          <button
            key={mode.value}
            type="button"
            role="tab"
            aria-selected={expandMode === mode.value}
            className={`wf-editor-aiprod__mode-btn${expandMode === mode.value ? ' wf-editor-aiprod__mode-btn--active' : ''}`}
            disabled={readOnly}
            onClick={() => setMode(mode.value)}
          >
            {mode.value === 2 ? t('workflowParams.byRatio') : t('workflowParams.byScale')}
          </button>
        ))}
      </div>

      {isScaleMode ? (
        <div className="outpaint-expand-params__panel">
          <div className="img2img-studio__field-row">
            <span className="img2img-studio__field-label">{t('workflowParams.scale')}</span>
            <span className="img2img-studio__field-hint">{t('workflowParams.scaleHint', { min: OUTPAINT_SCALE_MIN, max: OUTPAINT_SCALE_MAX })}</span>
          </div>
          <div className="outpaint-expand-params__scale-card">
            <div className="outpaint-expand-params__scale-grid" role="group" aria-label={t('workflowParams.scaleAria')}>
              {OUTPAINT_SCALE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`outpaint-expand-params__scale-btn${!isCustomScale && scale === opt.value ? ' is-active' : ''}`}
                  onClick={() => handleScalePreset(opt.value)}
                  disabled={readOnly}
                  aria-pressed={!isCustomScale && scale === opt.value}
                >
                  {opt.label}
                </button>
              ))}
              <button
                type="button"
                className={`outpaint-expand-params__scale-btn outpaint-expand-params__scale-btn--custom${isCustomScale ? ' is-active' : ''}`}
                onClick={handleScaleCustom}
                disabled={readOnly}
                aria-pressed={isCustomScale}
              >
                {t('workflowParams.custom')}
              </button>
            </div>
            {isCustomScale ? (
              <div className="outpaint-expand-params__scale-custom">
                <input
                  type="number"
                  className="outpaint-expand-params__scale-input"
                  min={OUTPAINT_SCALE_MIN}
                  max={OUTPAINT_SCALE_MAX}
                  step="0.01"
                  value={scaleDraft}
                  placeholder={t('workflowParams.scalePh')}
                  disabled={readOnly}
                  aria-label={t('workflowParams.scaleCustomAria')}
                  onChange={handleScaleInput}
                  onBlur={commitScaleDraft}
                />
                <p className="outpaint-expand-params__hint">
                  {t('workflowParams.scaleExample', { min: OUTPAINT_SCALE_MIN, max: OUTPAINT_SCALE_MAX })}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="outpaint-expand-params__panel">
          <div className="img2img-studio__field-row">
            <span className="img2img-studio__field-label">{t('workflowParams.resultRatio')}</span>
            <span className="img2img-studio__field-hint">{t('workflowParams.resultRatioHint')}</span>
          </div>
          <div className="outpaint-expand-params__ar-grid" role="list" aria-label={t('workflowParams.ratioPresets')}>
            {OUTPAINT_AR_PRESETS.map((item) => (
              <button
                key={item.value}
                type="button"
                role="listitem"
                className={`outpaint-expand-params__ar-item${aspectRatio === item.value ? ' is-active' : ''}`}
                disabled={readOnly}
                aria-pressed={aspectRatio === item.value}
                onClick={() => selectAr(item.value)}
              >
                <RatioWireIcon w={item.w} h={item.h} />
                <span>{item.value}</span>
              </button>
            ))}
            <button
              type="button"
              role="listitem"
              className={`outpaint-expand-params__ar-item${isCustomAr ? ' is-active' : ''}`}
              disabled={readOnly}
              aria-pressed={isCustomAr}
              onClick={() => selectAr('custom')}
            >
              <RatioWireIcon w={iconRatio.w} h={iconRatio.h} />
              <span>{t('workflowParams.custom')}</span>
            </button>
          </div>
          {isCustomAr ? (
            <div className="outpaint-expand-params__custom-block">
              <div className="outpaint-expand-params__custom-row">
                <label className="outpaint-expand-params__custom-dim">
                  <span>{t('workflowParams.width')}</span>
                  <input
                    type="number"
                    min={OUTPAINT_CUSTOM_AR_MIN}
                    max={OUTPAINT_CUSTOM_AR_MAX}
                    step="1"
                    className="outpaint-expand-params__custom-input"
                    value={value?.outputWidth ?? ''}
                    disabled={readOnly}
                    onChange={handleWidthChange}
                    aria-label={t('workflowParams.widthAria')}
                  />
                </label>
                <span className="outpaint-expand-params__custom-sep" aria-hidden="true">:</span>
                <label className="outpaint-expand-params__custom-dim">
                  <span>{t('workflowParams.height')}</span>
                  <input
                    type="number"
                    min={OUTPAINT_CUSTOM_AR_MIN}
                    max={OUTPAINT_CUSTOM_AR_MAX}
                    step="1"
                    className="outpaint-expand-params__custom-input"
                    value={value?.outputHeight ?? ''}
                    disabled={readOnly}
                    onChange={handleHeightChange}
                    aria-label={t('workflowParams.heightAria')}
                  />
                </label>
              </div>
              <p className="outpaint-expand-params__hint">
                {t('workflowParams.ratioInt', { min: OUTPAINT_CUSTOM_AR_MIN, max: OUTPAINT_CUSTOM_AR_MAX })}
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
